import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { api } from "../utils/apiService";
import { useTranslation } from "../hooks/useTranslation";

// ─── Shared style tokens ─────────────────────────────────────────────────────

const controlStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "2px",
    height: 28,
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    fontSize: "0.875rem",
  },
};

// ─── Status chip helper ───────────────────────────────────────────────────────

function StatusChip({ status }) {
  const isActive =
    typeof status === "string" && status.toLowerCase() === "active";

  return (
    <Chip
      label={status || "—"}
      size="small"
      variant="outlined"
      color={isActive ? "success" : "error"}
      sx={{
        fontWeight: "medium",
        fontSize: "0.75rem",
        height: 22,
        backgroundColor: !isActive ? "rgba(211, 47, 47, 0.1)" : "transparent",
      }}
    />
  );
}

const thSx = {
  fontWeight: "medium",
  color: "text.secondary",
  py: 0.5,
  px: 1.5,
  whiteSpace: "nowrap",
};

const tdSx = {
  fontSize: "0.875rem",
  py: 0.5,
  px: 1.5,
};

export default function MyCompany() {
  const { enqueueSnackbar } = useSnackbar();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { t, language } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(0);
    }, 400);
  }, []);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCompanies = async () => {
      try {
        setLoading(true);

        const params = { limit: "all" };
        if (searchQuery.trim()) {
          params.search = searchQuery.trim();
        }

        const res = await api.get("/vat-admin/my-companies", { params });
        if (cancelled) return;

        const records = res.data?.data ?? res.data ?? [];
        setCompanies(Array.isArray(records) ? records : []);
      } catch (error) {
        if (cancelled) return;
        console.error("MyCompany: Failed to fetch companies:", error);

        if (error.response?.status === 401) {
          enqueueSnackbar("Session expired. Please log in again.", {
            variant: "error",
          });
        } else {
          enqueueSnackbar(
            error.response?.data?.message || "Failed to load companies",
            { variant: "error" },
          );
        }
        setCompanies([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompanies();

    return () => {
      cancelled = true;
    };
  }, [searchQuery, enqueueSnackbar]);

  const paginatedCompanies = companies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t("sidebar.My_Company")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t("sidebar.My_Company_Description")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("sidebar.My_Company_Search")}
            value={searchInput}
            onChange={handleSearchChange}
            id="my-company-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              ...controlStyle,
              width: "100%",
              maxWidth: 500,
            }}
          />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "4px",
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ ...thSx, width: "48px" }}>
                    #
                  </TableCell>
                  <TableCell align="left" sx={{ ...thSx, minWidth: "200px" }}>
                    {t("Common.Company_Name")}
                  </TableCell>
                  <TableCell align="left" sx={{ ...thSx, minWidth: "180px" }}>
                    {t("Common.VAT_Number")}
                  </TableCell>
                  <TableCell align="left" sx={{ ...thSx, minWidth: "150px" }}>
                    {t("Sales.City")}
                  </TableCell>
                  <TableCell align="left" sx={{ ...thSx, minWidth: "200px" }}>
                    {t("CUSTOMERS.Email")}
                  </TableCell>
                  <TableCell align="center" sx={{ ...thSx, minWidth: "100px" }}>
                    {t("Reports.Status")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      {searchQuery
                        ? `No companies found matching "${searchQuery}".`
                        : "No company records available."}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCompanies.map((company, index) => (
                    <TableRow
                      key={company.tenantId ?? index}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        align="left"
                        sx={{ ...tdSx, color: "text.secondary" }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ ...tdSx, fontWeight: "medium" }}
                      >
                        {company.name || "—"}
                      </TableCell>
                      <TableCell align="left" sx={tdSx}>
                        {company.vatNo || "—"}
                      </TableCell>
                      <TableCell align="left" sx={tdSx}>
                        {company.city || "—"}
                      </TableCell>
                      <TableCell align="left" sx={tdSx}>
                        {company.email || "—"}
                      </TableCell>
                      <TableCell align="center" sx={tdSx}>
                        <StatusChip status={company.status} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={companies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              "& .MuiTablePagination-select": {
                py: 0.5,
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
}
